import { useNavigate } from "@tanstack/react-router";
import StatusBadge from "./StatusBadge";
import type { ListApplication } from "~/types/application.types";
import { formatDate } from "~/utils/date-formatter";
import { getRoleDisplayName } from "~/utils/role-formatter";
import type { CollaboratorRole } from "~/types/application.types";
import { Button } from "../ui/button";
import { Trash2, Lock, User } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { useState } from "react";
import { Spinner } from "../ui/spinner";
import { Separator } from "../ui/separator";
import { useTenantPath } from "~/hooks/use-tenant-path";

type Application = ListApplication;

type ApplicationCardProps = {
  application: Application;
  isCollaboratedView?: boolean;
};

export default function ApplicationCard({
  application,
  isCollaboratedView = false,
}: ApplicationCardProps) {
  // Check ownership - if isOwner is undefined, assume it's the user's own application
  const isOwner = (application as Application & { isOwner?: boolean }).isOwner !== false;
  const ownerName = (application as Application & { ownerName?: string | null }).ownerName;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantPath = useTenantPath();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dashboardTitle =
    application.category === "WRITER" ? "" : application.title;

  const { mutate: deleteApplication, isPending: isDeletingApplication } =
    useMutation({
      mutationFn: async () => {
        return apiClient.applications.delete({
          id: application.id,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.APPLICATIONS_LIST,
        });
        setIsModalOpen(false);
        navigate({ to: tenantPath("/dashboard") });
      },
      onError: (error: Error) => {
        console.error(
          error.message || "Failed to delete application. Please try again."
        );
        setIsModalOpen(false);
      },
    });

  const formatCategory = (category: Application["category"]) => {
    if (!category) return "-";
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  const handleCardClick = () => {
    const isInProgress =
      !isCollaboratedView && application.applicationStatus === "IN_PROGRESS";
    const canEdit = isOwner && isInProgress;

    if (canEdit) {
      navigate({
        to: tenantPath("/apply/$applicationId"),
        params: { applicationId: String(application.id) },
      });
    } else {
      // View-only mode for non-owned or completed applications
      navigate({ to: tenantPath(`/applications/${application.id}`) });
    }
  };

  return (
    <div
      className="bg-card border border-border rounded-lg p-4 hover:bg-primary/10 transition-colors cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-foreground text-lg text-wrap flex items-center gap-2">
            {!isOwner && <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />}
            {dashboardTitle || (
              <span className="text-foreground/70 font-light text-base">
                {application.category === "WRITER" ? "" : "No title"}
              </span>
            )}
          </h3>
          {!isOwner && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full flex-shrink-0 font-medium">
              View only
            </span>
          )}
        </div>

        <Separator />

        <div className="flex flex-col gap-1 text-sm">
          {/* Show owner for org applications */}
          {ownerName && (
            <div className="flex items-center gap-2">
              <span className="text-foreground/80 font-medium">Owner:</span>
              <span className="text-foreground">{ownerName}</span>
            </div>
          )}
          {isOwner && "isOwner" in application && (
            <div className="flex items-center gap-2">
              <span className="text-foreground/80 font-medium">Owner:</span>
              <span className="inline-flex items-center gap-1 text-brand-green-primary font-medium">
                <User className="w-3.5 h-3.5" />
                You
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-foreground/80 font-medium">Category:</span>
            <span className="font-semibold text-foreground">
              {formatCategory(application.category)}
            </span>
          </div>

          {isCollaboratedView && (
            <div className="flex items-center gap-2">
              <span className="text-foreground/80 font-medium">Role:</span>
              <span className="text-foreground">
                {(application as Application & { collaboratorRole?: string })
                  .collaboratorRole
                  ? getRoleDisplayName(
                      (
                        application as Application & {
                          collaboratorRole?: string;
                        }
                      ).collaboratorRole as CollaboratorRole
                    )
                  : "-"}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-foreground/80 font-medium">Status:</span>
            <StatusBadge status={application.applicationStatus} />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-foreground/80 font-medium">Last Updated:</span>
            <span className="font-medium text-foreground">
              {formatDate(application.updatedAt)}
            </span>
          </div>
        </div>

        {/* Only show delete for owned, in-progress applications */}
        {isOwner && application.applicationStatus === "IN_PROGRESS" && (
          <div
            className="mt-2 pt-2 border-t border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:text-red-500 hover:bg-transparent focus:ring-none p-0"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Application</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete the application to certify
                    {dashboardTitle ? (
                      <>
                        {" "}<span className="font-semibold text-foreground">{dashboardTitle}</span>
                        {application.authorName && (
                          <> by <span className="font-semibold text-foreground">{application.authorName}</span></>
                        )}
                      </>
                    ) : null}
                    ? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    onClick={() => deleteApplication()}
                    disabled={isDeletingApplication}
                  >
                    {isDeletingApplication ? (
                      <Spinner className="size-4" />
                    ) : (
                      "Delete"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
}
